import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * The case count, as one line.
 *
 * Was three things stacked: a bold total, the words "Total Cases", and a badge
 * per active filter. The badges restated what the selected filter pills already
 * showed one row below — the same fact twice, in two visual languages, in the
 * narrowest column on the screen.
 *
 * Filtering now reads as "18 of 655", which says both numbers in the space the
 * total alone used to take, and makes the filter's effect legible without
 * naming the filter again.
 */
@Component({
  selector: 'app-case-stats-bar',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="count" [attr.aria-label]="ariaLabel()">
      <span class="count-value">{{ shownCount() | number }}</span>
      @if (isFiltered()) {
        <span class="count-of">of {{ totalCount() | number }}</span>
      }
    </span>
  `,
  styles: [
    `
      .count {
        display: inline-flex;
        align-items: baseline;
        gap: 4px;
        font-variant-numeric: tabular-nums;
      }

      .count-value {
        font-size: 0.875rem;
        font-weight: 600;
        color: #101828;
      }

      .count-of {
        font-size: 0.75rem;
        color: #98a2b3;
      }
    `,
  ],
})
export class CaseStatsBarComponent {
  totalCount = input.required<number>();
  filteredCount = input.required<number>();
  activePriorityFilter = input<string | null>(null);
  activeStatusFilter = input<string | null>(null);

  isFiltered = computed(() => !!(this.activePriorityFilter() || this.activeStatusFilter()));

  shownCount = computed(() => (this.isFiltered() ? this.filteredCount() : this.totalCount()));

  ariaLabel = computed(() =>
    this.isFiltered()
      ? `${this.filteredCount()} of ${this.totalCount()} cases match the current filters`
      : `${this.totalCount()} cases`,
  );
}
